import { useEffect, useReducer, type Reducer } from 'react';
import { useIntl } from '@atlassian/jira-intl';
import type { FieldOption } from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { JQLEditorUIWithErrorBoundary$data } from '../__generated__/JQLEditorUIWithErrorBoundary.graphql';
import type { FieldToRender } from '../../../common/types.tsx';
import { useSearchState } from '../../../controllers/search-state/index.tsx';
import { gatherFields } from '../../../utils/gather-fields/main.tsx';
import { getSelectedFields } from '../../../utils/get-selected-fields/index.tsx';
import type { IrremovableFields } from '../../../utils/irremovable-fields/index.tsx';
import { useLocaleCompare } from '../../../utils/use-locale-compare/index.tsx';

export type Props = {
	/**
	 * query is the jql query string
	 * we use it to generate the fieldsToRender and unsupportedFields
	 * and to detect when the query changed
	 */
	query: string;

	/**
	 * loading is used to detect when we need to show the loading state
	 * eg external jql changed and we are waiting for hydrated query
	 */
	loading: boolean;

	/**
	 * fieldsHydrationData is the data used to hydrate the fieldsToRender
	 */
	fieldsHydrationData: JQLEditorUIWithErrorBoundary$data;

	/**
	 * excludedFields is the array of fieldIds to exclude from the fieldsToRender
	 */
	excludedFields?: string[];

	/**
	 *  irremovable fields to concatinate with selected optional fields to form the final fieldsToRender
	 */
	irremovableFields: IrremovableFields;
};

export type State = {
	/**
	 * InternalJql is used to track when query really changed
	 */
	internalJql: string;

	/**
	 * fieldsToRender is the array generated by gatherFields that we use to build the pickers
	 */
	fieldsToRender: FieldToRender[];

	/**
	 * selectedFields is the array generated by getSelectedFields that we use to track selected fields in the AddFiler dropdown
	 */
	selectedFields: FieldOption[];

	/**
	 * optionalFieldsCount is the count of optional fields generated by gatherFields
	 */
	optionalFieldsCount: number;

	/**
	 * showLoadingState detects when we need to show the loading state eg external jql changed and we are waiting for hydrated
	 * to know which pickers to show, but also knows when not to show it if we internaly change the jql and trigger another hydrated
	 */
	showLoadingState: boolean;
};

export const useFieldsToRender = ({
	query,
	loading,
	fieldsHydrationData,
	excludedFields,
	irremovableFields,
}: Props) => {
	const intl = useIntl();
	const comparator = useLocaleCompare();
	const [{ astClauseOrder, clausesByFieldId, secondaryClausesByFieldId }] = useSearchState();

	const [internalState, setInternalState] = useReducer<Reducer<State, Partial<State>>, State>(
		(oldState, newState) => ({ ...oldState, ...newState }),
		{
			internalJql: query,
			fieldsToRender: [],
			selectedFields: [],
			optionalFieldsCount: 0,
			showLoadingState: loading,
		},
		(initialState) => {
			const { internalJql, showLoadingState } = initialState;
			const { fieldsToRender, optionalFieldsCount } = gatherFields({
				selectedFields: [],
				excludedFields,
				intl,
				fieldsHydratedData: fieldsHydrationData,
				irremovableFields,
				astClauseOrder,
				clausesByFieldId,
				secondaryClausesByFieldId,
			});

			const selectedFields = getSelectedFields({
				fieldsToRender,
				intl,
				comparator,
				irremovableFields,
			});

			return {
				internalJql,
				fieldsToRender,
				selectedFields,
				optionalFieldsCount,
				showLoadingState,
			};
		},
	);

	useEffect(
		() => {
			const { fieldsToRender, selectedFields, internalJql } = internalState;
			const isExternalChange = internalJql !== query;

			const {
				fieldsToRender: newFieldsToRender,
				optionalFieldsCount,
				missingHydration,
			} = gatherFields({
				selectedFields: isExternalChange ? [] : selectedFields,
				excludedFields,
				intl,
				fieldsHydratedData: fieldsHydrationData,
				previousFieldToRender: !isExternalChange ? fieldsToRender : [],
				irremovableFields,
				astClauseOrder,
				clausesByFieldId,
				secondaryClausesByFieldId,
			});

			const newSelectedFields = getSelectedFields({
				fieldsToRender: newFieldsToRender,
				intl,
				comparator,
				irremovableFields,
			});

			setInternalState({
				showLoadingState: missingHydration,
				fieldsToRender: newFieldsToRender,
				selectedFields: newSelectedFields,
				optionalFieldsCount,
				...(isExternalChange
					? {
							internalJql: query,
						}
					: {}),
			});
		},
		// When the query changes, we want to ensure fieldsToRender and selectedFields display as desired.
		// To prevent extraneous calls to useEffect, we leave out all dependencies besides query and fieldsHydrationData.
		// Where required, selectedFields state will be updated before query is.
		// We also assume that ast only changes when query also does.

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[query, fieldsHydrationData],
	);

	return {
		internalState,
		setInternalState,
	};
};
